// PropertyDetails.jsx — Complete fix
// ✅ property_id auto-show
// ✅ extra_fields dynamic display
// ✅ EnquiryModal proper
// ✅ Responsive

import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import PropertyCard from './PropertyCard';
import './PropertyDetails.css';

import { MyContext } from '../../../../../Context/MyContext';
import { propertyActivityLabel } from '../../../../../utils/userActivity';
import BookingModal from './BookingModal';
import AppIcon from '../../../../../Components/AppIcon';
import dtcpBadge from '../../../../../Assets/dtcp.png';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SharePopup } from './PropertyCard';
import EnquiryModal from './EnquiryModal';
import ImageWithLoader from "../../../../../Config/ImageWithLoader";
import ContactModal from './ContactModal';
import { buildPropertyWebShareUrl, warmPropertyShareCover } from '../../../../utils/propertyShareUrl';
import { buildCanonicalUrl, setPageMeta } from '../../../../../utils/seo';
import PropertyAppPrompt from '../../../../../Components/PropertyAppPrompt';
import { isCustomDropdownValue, decodeCustomDropdownValue } from '../../../../../utils/dropdownCustomValue';
import { formatTotalFloors } from '../../../../../utils/formatTotalFloors';
import { isListingActive, isSoldOut, LISTING_INACTIVE_MSG, SOLD_OUT_MSG } from '../../../../../utils/propertyStatus';
import { extractYoutubeVideoId, getYoutubeEmbedUrl, youtubeThumbnail } from '../../../../../utils/youtubeEmbed';
import { formatStateInCity } from '../../../../../utils/locationFormat';
import { buildKnownCategoryIdSet, isDetailFieldVisible, isLandTypeDropdownVisible } from '../../../../../utils/detailFieldVisibility';
import { getLandtypeIdsForAreaField } from '../../../../../utils/landAreaConfig';
// ── Dropdown label resolver ─────────────────────────────────


// const isMobile = window.innerWidth <= 768;

const isEmptyDisplayValue = (v) => {
  if (v === null || v === undefined || v === false) return true;
  if (typeof v === 'number' && v === 0) return true;
  if (typeof v === 'string' && (v.trim() === '' || v.trim() === '0')) return true;
  return false;
};

const ok = (v) => !isEmptyDisplayValue(v);

const hasDetailCellValue = (value, type) => {
  if (type === 'bool') {
    if (value === null || value === undefined || value === false) return false;
    if (typeof value === 'number' && value === 0) return false;
    if (typeof value === 'string' &&
      (value.trim() === '' || value.trim() === '0' ||
        value.toLowerCase() === 'false' || value.toLowerCase() === 'no')) return false;
    return true;
  }
  return ok(value);
};

/** Yes/No fields stored as 0 or 1 (or boolean). */
const formatYesNo = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (v === true || v === 1 || v === '1') return 'Yes';
  if (v === false || v === 0 || v === '0') return 'No';
  return null;
};

const resolveLabel = (dropdown, dropdown_id, value) => {
  if (isEmptyDisplayValue(value)) return null;
  if (isCustomDropdownValue(value)) return decodeCustomDropdownValue(value);
  const dd = dropdown.find(d => d.id === dropdown_id);
  if (!dd) return String(value);

  // Match by value (web-created properties store option.value)
  let opt = dd.options?.find(o => String(o.value).trim() === String(value).trim());

  // Match by id (app-created properties store option.id as number)
  if (!opt) opt = dd.options?.find(o => Number(o.id) === Number(value));

  // Match by label (legacy fallback)
  if (!opt) opt = dd.options?.find(
    o => String(o.label).trim().toLowerCase() === String(value).trim().toLowerCase()
  );

  return opt?.label || String(value);
};

const normalizeNearestLocation = (raw) => {
  if (!raw) return [];
  let data = raw;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return [];
    }
  }

  const pushRow = (rows, item) => {
    if (!item || typeof item !== 'object') return;
    const nearId = item.near_id ?? item.nearest_location_id;
    const distance = item.distance ?? item.near_value;
    if (isEmptyDisplayValue(nearId) || isEmptyDisplayValue(distance)) return;
    rows.push({ nearId, distance: String(distance).trim() });
  };

  if (Array.isArray(data)) {
    const rows = [];
    data.forEach((item) => pushRow(rows, item));
    return rows;
  }

  if (typeof data === 'object') {
    const rows = [];
    Object.values(data).forEach((items) => {
      if (Array.isArray(items)) items.forEach((item) => pushRow(rows, item));
    });
    return rows;
  }

  return [];
};

// ── Utils ────────────────────────────────────────────────────
const fmt = (n) => {
  if (isEmptyDisplayValue(n)) return null;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

/** Prefer real address text; never show map URLs as the Address value. */
const resolveAddressDisplay = (property, extra_fields) => {
  const candidates = [
    property?.address,
    extra_fields?.address,
    property?.map_location_name,
  ];
  for (const raw of candidates) {
    if (raw == null) continue;
    const text = String(raw).trim();
    if (!text) continue;
    if (/^https?:\/\//i.test(text) || /^www\./i.test(text)) continue;
    return text;
  }
  return null;
};


const getPriceDisplay = (property) => {
  const { price, price_from, price_to } = property;

  if (ok(price)) return fmt(price);

  if (ok(price_from) && ok(price_to)) {
    return `${fmt(price_from)} - ${fmt(price_to)}`;
  }

  if (ok(price_from)) {
    return `From ${fmt(price_from)}`;
  }

  if (ok(price_to)) {
    return `Up to ${fmt(price_to)}`;
  }

  return null;
};

const getYTId = (url) => extractYoutubeVideoId(url);




// ── YouTube Modal ────────────────────────────────────────────
const YTModal = ({ videoId, onClose }) => {
  if (!videoId) return null;
  return (
    <div className="ytm-back" onClick={onClose}>
      <div className="ytm-box" onClick={e => e.stopPropagation()}>
        <button className="ytm-close" onClick={onClose} aria-label="Close">
          <AppIcon name="close" size={18} />
        </button>
        <div className="ytm-frame">
          <iframe
            src={getYoutubeEmbedUrl(videoId, { autoplay: true })}
            title="video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      </div>
    </div>
  );
};

// ── Story Viewer ─────────────────────────────────────────────
const StoryViewer = ({ links, startIdx, onClose }) => {
  const [idx, setIdx] = useState(startIdx);
  const [key, setKey] = useState(0);
  const total = links.length;
  const vid = getYTId(links[idx]);

  const go = useCallback((n) => {
    if (n < 0) { if (idx === 0) onClose(); else { setIdx(i => i - 1); setKey(k => k + 1); } }
    else { if (idx === total - 1) onClose(); else { setIdx(i => i + 1); setKey(k => k + 1); } }
  }, [idx, total, onClose]);

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [go, onClose]);

  return (
    <div className="sv-back">
      <div className="sv-wrap">
        <div className="sv-bars">
          {links.map((_, i) => (
            <div key={i} className="sv-bar-track">
              <div className={`sv-bar-fill${i < idx ? ' sv-done' : i === idx ? ' sv-active' : ''}`} key={i === idx ? key : i} />
            </div>
          ))}
        </div>
        <div className="sv-header">
          <div className="sv-hinfo">
            <div className="sv-hav"><AppIcon name="home" size={18} /></div>
            <div>
              <div className="sv-hname">Property Short</div>
              <div className="sv-hnum">{idx + 1} of {total}</div>
            </div>
          </div>
          <button className="sv-close" onClick={onClose} aria-label="Close">
            <AppIcon name="close" size={18} />
          </button>
        </div>
        <div className="sv-media">
          {vid
            ? <iframe
              key={key}
              src={getYoutubeEmbedUrl(vid, { autoplay: true })}
              title="short"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              className="sv-iframe"
            />
            : <div className="sv-placeholder"><div className="sv-ph-icon"><AppIcon name="video" size={24} /></div><div className="sv-ph-text">Short {idx + 1}</div></div>
          }
        </div>
        <div className="sv-tap sv-tap-l" onClick={() => go(-1)} />
        <div className="sv-tap sv-tap-r" onClick={() => go(1)} />
      </div>
    </div>
  );
};

// ── Lightbox ──────────────────────────────────────────────────
const Lightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => (i - 1 + images.length) % images.length);
  const next = () => setIdx(i => (i + 1) % images.length);
  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [idx]); // eslint-disable-line
  return (
    <div className="lb-back" onClick={onClose}>
      <button className="lb-x" onClick={onClose} aria-label="Close">
        <AppIcon name="close" size={20} />
      </button>
      <button className="lb-nav lb-l" onClick={e => { e.stopPropagation(); prev(); }}>‹</button>
      <img src={images[idx]} alt="" className="lb-img" onClick={e => e.stopPropagation()} />
      <button className="lb-nav lb-r" onClick={e => { e.stopPropagation(); next(); }}>›</button>
      <div className="lb-cnt">{idx + 1} / {images.length}</div>
    </div>
  );
};

// ── Gallery ───────────────────────────────────────────────────
const Gallery = ({ images }) => {
  const [active, setActive] = useState(0);
  const [lb, setLb] = useState(false);
  const touchX = useRef(0);
  const vertRef = useRef(null);
  const horizRef = useRef(null);

  const prev = useCallback(() => setActive(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setActive(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    [vertRef, horizRef].forEach(ref => {
      if (ref.current) {
        const el = ref.current.children[active];
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    });
  }, [active]);

  const MAX = 6;
  const extras = images.length > MAX ? images.length - MAX : 0;

  return (
    <>
      <div className="gal">
        <div className="gal-main"
          onClick={() => setLb(true)}
          onTouchStart={e => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            const d = touchX.current - e.changedTouches[0].clientX;
            if (d > 40) next(); else if (d < -40) prev();
          }}
        >
          <ImageWithLoader src={images[active]} alt="" className="gal-img" />
          {images.length > 1 && (
            <>
              <button className="gal-arr gal-l" onClick={e => { e.stopPropagation(); prev(); }}>‹</button>
              <button className="gal-arr gal-r" onClick={e => { e.stopPropagation(); next(); }}>›</button>
            </>
          )}
          <span className="gal-counter">{active + 1} / {images.length}</span>
          <span className="gal-expand">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
            View all
          </span>
        </div>
        <div className="gal-vert" ref={vertRef}>
          {images.slice(0, MAX).map((img, i) => {
            const isLast = i === MAX - 1 && extras > 0;
            return (
              <div key={i} className={`gal-vt${active === i ? ' active' : ''}`} onClick={() => setActive(i)}>
                <img src={img} alt="" />
                {isLast && <div className="gal-more" onClick={e => { e.stopPropagation(); setLb(true); }}>+{extras}</div>}
              </div>
            );
          })}
        </div>
        <div className="gal-horiz" ref={horizRef}>
          {images.map((img, i) => (
            <div key={i} className={`gal-ht${active === i ? ' active' : ''}`} onClick={() => setActive(i)}>
              <img src={img} alt="" />
            </div>
          ))}
        </div>
      </div>
      {lb && <Lightbox images={images} startIndex={active} onClose={() => setLb(false)} />}
    </>
  );
};

// ── Area Row ──────────────────────────────────────────────────
const getLandtypeName = (unitId, landtype) => {
  if (!unitId || !landtype?.length) return null;
  const lt = landtype.find((l) => Number(l.id) === Number(unitId));
  return lt?.landtype_name || null;
};

const toSqft = (value, unitId, landtype) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return null;
  if (!unitId || !landtype?.length) return num;
  const lt = landtype.find((l) => Number(l.id) === Number(unitId));
  if (!lt?.calculation?.to_sqft) return num;
  return num * lt.calculation.to_sqft;
};

const numOrNull = (v) => {
  if (!ok(v)) return null;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
};

const normalizeDocFiles = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val
      .map((item) => {
        if (!item) return null;
        if (typeof item === 'string') return item.trim() || null;
        if (typeof item === 'object') {
          const path = item.path || item.url || item.src || item.default || item.file;
          return typeof path === 'string' && path.trim() ? path.trim() : null;
        }
        return null;
      })
      .filter(Boolean);
  }
  if (typeof val === 'string' && val.trim()) return [val.trim()];
  if (typeof val === 'object') {
    const path = val.path || val.url || val.src || val.default || val.file;
    if (typeof path === 'string' && path.trim()) return [path.trim()];
  }
  return [];
};

const isPdfFile = (file, typeHint) => {
  if (typeof file === 'string') {
    if (/\.pdf($|\?)/i.test(file)) return true;
    if (/\.(jpe?g|png|gif|webp|bmp|svg)($|\?)/i.test(file)) return false;
  }
  return String(typeHint || '').toLowerCase() === 'pdf';
};

const fileBaseName = (file) => {
  if (typeof file !== 'string') return 'Document';
  try {
    return decodeURIComponent(file.split('/').pop().split('?')[0]) || 'Document';
  } catch {
    return file.split('/').pop() || 'Document';
  }
};

const RangeAreaRow = ({ label, from, to, unitId }) => {
  const { landtype } = useContext(MyContext);
  const fromN = numOrNull(from);
  const toN = numOrNull(to);
  if (fromN == null && toN == null) return null;

  const unitName = getLandtypeName(unitId, landtype) || '';
  const fmtN = (n) => Number(n).toLocaleString('en-IN');
  let valueText;
  if (fromN != null && toN != null && fromN !== toN) {
    valueText = `${fmtN(fromN)} – ${fmtN(toN)}`;
  } else {
    valueText = fmtN(fromN ?? toN);
  }

  return (
    <div className="det-row">
      <span className="det-lbl">{label}</span>
      <div className="det-rhs">
        <span className="det-val">
          {valueText}{unitName ? ` ${unitName}` : ''}
        </span>
      </div>
    </div>
  );
};

const DocMediaSection = ({
  title,
  files,
  index,
  setIndex,
  typeHint,
  imgUrl,
  showDownloadAll = false,
  onDownloadAll,
}) => {
  if (!files?.length) return null;
  const safeIndex = Math.min(Math.max(index, 0), files.length - 1);
  const file = files[safeIndex];
  const url = imgUrl ? imgUrl(file) : file;
  const pdf = isPdfFile(file, typeHint);

  return (
    <section className="pd-sec">
      <div className="sec-head">{title}</div>
      <div className="brochure-card">
        <div className={`brochure-main${pdf ? ' brochure-main-pdf' : ''}`}>
          {pdf ? (
            <div className="brochure-pdf-fallback">
              <AppIcon name="pdf" size={40} />
              <div className="brochure-pdf-name" title={fileBaseName(file)}>
                {fileBaseName(file)}
              </div>
              <div className="brochure-pdf-actions">
                <a
                  className="brochure-pdf-btn"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open PDF
                </a>
                <a className="brochure-pdf-btn is-secondary" href={url} download>
                  Download
                </a>
              </div>
            </div>
          ) : (
            <img
              src={url}
              alt={title}
              className="brochure-img"
              onError={(e) => {
                e.currentTarget.style.opacity = '0.35';
              }}
            />
          )}
          <span className="brochure-type-badge">{pdf ? 'PDF' : 'Image'}</span>
          {files.length > 1 && (
            <div className="brochure-controls">
              <button
                type="button"
                onClick={() => setIndex((prev) => (prev - 1 + files.length) % files.length)}
              >
                ‹
              </button>
              <span>{safeIndex + 1} / {files.length}</span>
              <button
                type="button"
                onClick={() => setIndex((prev) => (prev + 1) % files.length)}
              >
                ›
              </button>
            </div>
          )}
          {showDownloadAll && onDownloadAll && (
            <button type="button" className="brochure-download-icon" onClick={onDownloadAll} aria-label="Download all">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <path d="M12 3v12" />
                <path d="M7 10l5 5 5-5" />
                <rect x="4" y="18" width="16" height="2" rx="1" />
              </svg>
            </button>
          )}
        </div>
        {files.length > 1 && (
          <div className="brochure-thumbs">
            {files.map((f, i) => (
              <div
                key={`${f}-${i}`}
                className={`brochure-thumb${safeIndex === i ? ' active' : ''}`}
                onClick={() => setIndex(i)}
              >
                {isPdfFile(f, typeHint)
                  ? <div className="brochure-thumb-pdf">PDF</div>
                  : <img src={imgUrl ? imgUrl(f) : f} alt={`${title} ${i + 1}`} />
                }
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const AreaRow = ({ label, rawValue, unitId, totalPrice, allowedLandTypeIds, showLandTypeDropdown = true }) => {
  const { landtype } = useContext(MyContext);
  const allowedKey = Array.isArray(allowedLandTypeIds) ? allowedLandTypeIds.map(Number).join(',') : '';
  const units = useMemo(() => {
    const list = Array.isArray(landtype) ? landtype : [];
    if (!allowedKey) return list;
    const set = new Set(allowedKey.split(',').map(Number).filter(Number.isFinite));
    const filtered = list.filter((l) => set.has(Number(l.id)));
    return filtered.length ? filtered : list;
  }, [landtype, allowedKey]);

  const savedUnitId = unitId ? Number(unitId) : (units[0]?.id || 1);
  const [uid, setUid] = useState(savedUnitId || 1);

  useEffect(() => {
    const next = unitId ? Number(unitId) : (units[0]?.id || 1);
    setUid(next);
  }, [unitId, allowedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ok(rawValue)) return null;
  const selLT = units.find((l) => Number(l.id) === Number(uid)) || units[0] || landtype?.[0];
  const baseLT = (Array.isArray(landtype) ? landtype : []).find((l) => Number(l.id) === 1) || units[0];
  if (!selLT || !baseLT) return null;

  const sqftBase = toSqft(rawValue, savedUnitId || selLT.id, landtype);
  if (!sqftBase) return null;

  const toKey = `to_${String(selLT.landtype_name || '').replace(/-/g, '')}`;
  const factor = baseLT.calculation?.[toKey] ?? 1;
  const conv = +(sqftBase * factor).toFixed(3);
  const ppu = totalPrice && sqftBase
    ? Math.round(totalPrice / sqftBase * (selLT.calculation?.to_sqft ?? 1))
    : null;
  const savedName = getLandtypeName(savedUnitId, landtype);

  return (
    <div className="det-row">
      <span className="det-lbl">{label}</span>
      <div className="det-rhs">
        <span className="det-val">
          {Number(rawValue).toLocaleString('en-IN')}
          {savedName ? ` ${savedName}` : ''}
        </span>
        {showLandTypeDropdown && (
          <select className="det-sel" value={uid} onChange={(e) => setUid(Number(e.target.value))}>
            {units.map((l) => (
              <option key={l.id} value={l.id}>{l.landtype_name}</option>
            ))}
          </select>
        )}
        {showLandTypeDropdown && Number(uid) !== Number(savedUnitId) && (
          <span className="det-conv">≈ {conv} {selLT.landtype_name}</span>
        )}
        {ppu ? <span className="det-ppu">{fmt(ppu)}/{selLT.landtype_name}</span> : null}
      </div>
    </div>
  );
};

const DetRow = ({ label, value, unit }) => {
  if (!ok(value)) return null;
  const labelLower = String(label || '').toLowerCase();
  const unitStr = unit ? String(unit).trim() : '';
  // Avoid "Land (Cent): 5 Cent" when unit is already in the label
  const showUnit = unitStr && !labelLower.includes(unitStr.toLowerCase());
  return (
    <div className="det-row">
      <span className="det-lbl">{label}</span>
      <span className="det-val">{value}{showUnit ? ` ${unitStr}` : ''}</span>
    </div>
  );
};

const DetailCell = ({ label, value, type }) => {
  if (type === 'bool') {
    // Hide: null / undefined / false / 0 / "0" / "No" / "false"
    if (value === null || value === undefined || value === false) return null;
    if (typeof value === 'number' && value === 0) return null;
    if (typeof value === 'string' &&
      (value.trim() === '' || value.trim() === '0' ||
        value.toLowerCase() === 'false' || value.toLowerCase() === 'no')) return null;

    // Strict boolean true → "✓ Yes"
    if (value === true || value === 'Yes' || value === 'yes' ||
      value === 'true' || value === 'True') {
      return (
        <div className="det-cell">
          <div className="det-cell-lbl">{label}</div>
          <div className="det-cell-val det-yes"><AppIcon name="check" size={14} /> Yes</div>
        </div>
      );
    }

    // Numeric count (car_parking=1, bike_parking=2, etc.) → show the number
    return (
      <div className="det-cell">
        <div className="det-cell-lbl">{label}</div>
        <div className="det-cell-val">{value}</div>
      </div>
    );
  }

  if (!ok(value)) return null;
  return (
    <div className="det-cell">
      <div className="det-cell-lbl">{label}</div>
      <div className="det-cell-val">{String(value)}</div>
    </div>
  );
};

// ── Related Properties ────────────────────────────────────────
const PAGE_SIZE = 9;

const RelatedProperties = ({ currentPropertyId, categoryId }) => {
  const { fetchPropertiesPaged } = useContext(MyContext);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Initial load
  useEffect(() => {
    if (!categoryId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetchPropertiesPaged({
          category_id: categoryId,
          status: 2,
          page: 1,
          limit: PAGE_SIZE,
        });
        if (cancelled) return;
        const list = (res?.data || []).filter(p => String(p.id) !== String(currentPropertyId));
        setItems(list);
        // hasMore: if total > PAGE_SIZE OR there might be more after filtering current
        const total = res?.total ?? 0;
setHasMore((res?.pages ?? 1) > 1);
        setPage(1);
      } catch {
        // silently ignore
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [categoryId, currentPropertyId]);// eslint-disable-line

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await fetchPropertiesPaged({
        category_id: categoryId,
        status: 2,
        page: nextPage,
        limit: PAGE_SIZE,
      });
      const list = (res?.data || []).filter(p => String(p.id) !== String(currentPropertyId));
      setItems(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const fresh = list.filter(p => !existingIds.has(p.id));
        return [...prev, ...fresh];
      });
      const total = res?.total ?? 0;
setHasMore(nextPage < (res?.pages ?? 1));
      setPage(nextPage);
    } catch {
      // silently ignore
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="rp-section">
        <div className="rp-header">
          <h2 className="rp-title">Related Properties</h2>
        </div>
        <div className="rp-skeleton-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rp-skeleton-card">
              <div className="rp-sk-img" />
              <div className="rp-sk-body">
                <div className="rp-sk-line rp-sk-l1" />
                <div className="rp-sk-line rp-sk-l2" />
                <div className="rp-sk-line rp-sk-l3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div className="rp-section">
      <div className="rp-header">
        <h2 className="rp-title">Related Properties</h2>
        <span className="rp-count">{items.length}+ listings</span>
      </div>
      <div className="rp-grid">
        {items.map(p => (
          <PropertyCard key={p.id} property={p} />
        ))}
      </div>
    <div className="rp-loadmore-wrap">
  {hasMore ? (
    <button
      className="rp-loadmore-btn"
      onClick={handleLoadMore}
      disabled={loadingMore}
    >
      {loadingMore ? (
        <span className="rp-spinner" />
      ) : (
        <>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <polyline points="6 9 12 15 18 9" />
          </svg>
          Load More Properties
        </>
      )}
    </button>
  ) : (
    <p className="rp-no-more"> No more properties</p>
  )}
</div>
    </div>
  );
};

// ── MAIN ──────────────────────────────────────────────────────
const PropertyDetails = ({ property }) => {

  // const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // useEffect(() => {
  //   const handleResize = () => {
  //     setIsMobile(window.innerWidth <= 768);
  //   };

  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, []);

  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 768px)").matches
  );

  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");

    const listener = (e) => setIsMobile(e.matches);

    media.addEventListener("change", listener);

    return () => media.removeEventListener("change", listener);
  }, []);

  const {
    propertydetail, aminities, categorys, partners, admins,
    aminitiesCategories, dropdown, wishlist, toggleWishlist,
    imgUrl, propertyInputs, projectInputs, cities, states, nearestlocation,
  } = useContext(MyContext);

  const [modal, setModal] = useState({ open: false, title: '' });
  // const [showEnquiry, setShowEnquiry] = useState(false);
  const [ytId, setYtId] = useState(null);
  const [story, setStory] = useState({ open: false, idx: 0 });
  const [bookingOpen, setBookingOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [brochureIndex, setBrochureIndex] = useState(0);
  const [layoutIndex, setLayoutIndex] = useState(0);

  // Contacts — Fetch based on sub_register mapping
  const contacts = useMemo(() => {
    if (!property) return [];
    const { partner_id, extra_fields } = property;
    const sr_id = property.sub_register_id || extra_fields?.sub_register_id;

    // 1. Find partners assigned to this sub_register
    let matchedPartners = [];
    if (sr_id) {
      matchedPartners = partners?.filter((p) => {
        const pArr = Array.isArray(p.sub_registers)
          ? p.sub_registers
          : Array.isArray(p.sub_register)
            ? p.sub_register
            : p.sub_register != null
              ? [p.sub_register]
              : [];
        return pArr.map(Number).includes(Number(sr_id));
      });
    }

    if (matchedPartners?.length > 0) {
      return matchedPartners.map(p => ({
        name: p.name || p.partner_name,
        phone: p.phone || p.partner_phone,
        isAdmin: false
      }));
    }

    // 2. Global fallback to Admins (only those with show_user enabled)
    return (admins || [])
      .filter(a => a.show_user)
      .map(a => ({
        name: a.admin_name || a.name,
        phone: a.admin_phone || a.phone,
        isAdmin: true
      }));
  }, [partners, admins, property]);

  const navigate = useNavigate();
  const isWished = wishlist.includes(property?.id);

  useEffect(() => {
    if (!property) return;

    const cat = categorys?.find(c => c.id === property.category_id);
    const propLabel = cat?.category_name || 'Property';

    const city = cities?.find(c => c.id === property.city_id);
    const state = states?.find(s => s.id === property.state_id);
    const locText = formatStateInCity(state, city) || '';

    const title = `${propLabel}${locText ? ` in ${locText}` : ''}${getPriceDisplay(property) ? ` — ${getPriceDisplay(property)}` : ''} | Properties Infinite`;
    const pricePart = getPriceDisplay(property);
    const descParts = [];
    if (pricePart) descParts.push(`Price: ${pricePart}`);
    if (locText) descParts.push(`Location: ${locText}`);
    if (property.bhk) descParts.push(`${property.bhk} BHK`);
    const desc =
      property.seo_description ||
      (descParts.length
        ? `Check out this ${propLabel}${locText ? ` in ${locText}` : ''}. ${descParts.join(' · ')}`
        : `Check out this ${propLabel} on Properties Infinite.`);
    // Server OG card uses the listing photo (not company/site logo).
    const mediaKey = [
      property.updatedAt || property.date || '',
      property.thumbnail || '',
      Array.isArray(property.slider) ? property.slider[0] : '',
    ].join('|');
    const image = `https://propertiesinfinite.com/api/share/property/${property.id}/og-image?v=${encodeURIComponent(mediaKey)}`;
    const sharePageUrl = buildPropertyWebShareUrl({
      propertyId: property.id,
      categoryName: propLabel,
      cityName: city?.city_name,
    });

    const pathSlug = `${String(propLabel).toLowerCase().replace(/[^a-z0-9]+/g, '-')}-in-${String(city?.city_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`.replace(/(^-|-$)+/g, '') || 'view';
    const canonical = buildCanonicalUrl(`/property/${property.id}/${pathSlug}`);
    setPageMeta({ pathname: `/property/${property.id}/${pathSlug}`, title, description: desc, canonical });

    const setMeta = (name, content, attr = 'property') => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('og:title', title);
    setMeta('og:description', desc);
    setMeta('og:image', image);
    setMeta('og:image:secure_url', image);
    setMeta('og:image:width', '1200');
    setMeta('og:image:height', '630');
    setMeta('og:image:type', 'image/jpeg');
    setMeta('og:url', sharePageUrl);
    setMeta('og:type', 'website');
    setMeta('og:site_name', 'Properties Infinite');
    setMeta('twitter:card', 'summary_large_image', 'name');
    setMeta('twitter:title', title, 'name');
    setMeta('twitter:description', desc, 'name');
    setMeta('twitter:image', image, 'name');

    // Defer OG warm so it doesn't compete with first paint / gallery images.
    const warmTimer = window.setTimeout(() => {
      warmPropertyShareCover(property.id);
    }, 1800);

    return () => {
      window.clearTimeout(warmTimer);
      setPageMeta({ pathname: '/' });
      [
        'og:title', 'og:description', 'og:image', 'og:image:secure_url',
        'og:url', 'og:type', 'og:site_name',
      ].forEach(name => {
        const el = document.querySelector(`meta[property="${name}"]`);
        if (el) document.head.removeChild(el);
      });
      ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'].forEach(name => {
        const el = document.querySelector(`meta[name="${name}"]`);
        if (el) document.head.removeChild(el);
      });
    };
  }, [property, categorys, cities, states]);

  if (!property) return <div className="pd-empty">Property not found.</div>;

  const soldOut = isSoldOut(property);
  const listingActive = isListingActive(property);
  const ctaDisabled = !listingActive;

  const openAppSlug = `${categorys?.find((c) => c.id === property.category_id)?.category_name || 'property'}-in-${cities?.find((c) => c.id === property.city_id)?.city_name || ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '') || 'view';

  const {
    slider, thumbnail,
    is_urgent, is_highlighted, is_project, is_dtcp,
    category_id, type_id, price, location,
    bhk, direction, furnishing, gated_community,
    sales_type, property_availability, property_age,
    plot_length, plot_breadth,
    property_floor_no, total_floors,
    amenities_id, custom_amenities, partner_id,
    shorts_links, youtube_links, videos,
    property_id, date, property_plan,
    extra_fields, city_id, state_id, sub_register_id, brochure,
    layout, layout_type, brochure_type, nearest_location,
  } = property;

  const ef = extra_fields && typeof extra_fields === 'object' ? extra_fields : {};

  const brochureArr = normalizeDocFiles(brochure ?? ef.brochure);
  const layoutArr = normalizeDocFiles(layout ?? ef.layout);

  const city = cities?.find(c => c.id === city_id);
  const state = states?.find(s => s.id === state_id);

  const locationStr = formatStateInCity(state, city);

  // Images
  const resolveImg = (path) => imgUrl ? imgUrl(path) : path;
  let images = [];
  if (thumbnail) images.push(resolveImg(thumbnail));
  if (slider?.length) {
    slider.forEach(s => {
      const resolved = resolveImg(s);
      if (!images.includes(resolved)) images.push(resolved);
    });
  }

  const catName = categorys.find(c => c.id === category_id)?.category_name || '';

  // Dropdown labels
  const bhkLabel = extra_fields?.bhk_custom || resolveLabel(dropdown, 5, bhk);
  const directionLabel = resolveLabel(dropdown, 1, direction);
  const furnishingLabel = resolveLabel(dropdown, 3, furnishing);
  const availabilityLabel = resolveLabel(dropdown, 8, property_availability);
  const gatedLabel =
    formatYesNo(gated_community) ?? resolveLabel(dropdown, 2, gated_community);
  const floorLabel = extra_fields?.property_floor_no_custom
    || resolveLabel(dropdown, 6, property_floor_no);
  const totalFloorsLabel = formatTotalFloors(
    extra_fields?.total_floors_custom || total_floors
  );
  const ageValue =
    property.property_age ??
    property.extra_fields?.property_age;

  const propertyAgeLabel =
    resolveLabel(dropdown, 4, ageValue) ||
    ageValue;

  // PropertyDetail schema fields — respect admin enable/disable (enabled by default)
  const knownCategoryIds = buildKnownCategoryIdSet(categorys);
  const propertydetailList = (() => {
    const list = Array.isArray(propertydetail) ? [...propertydetail] : [];
    const ensure = (id, label, field) => {
      if (!list.some((d) => d.field === field)) {
        list.push({
          id, label, field, type: 'text', group: 'property',
          card_show: [], card_land_show: [],
          detail_show: [], project_detail_show: [],
        });
      }
    };
    ensure(12, 'Address', 'address');
    ensure(13, 'Address Details / Landmark', 'address_details');
    return list;
  })();
  const detailFields = propertydetailList.filter((d) =>
    isDetailFieldVisible(d, category_id, {
      isProject: !!is_project,
      knownCategoryIds,
    })
  );
  const areaFields = detailFields.filter((d) =>
    d.type === 'area' || ['plot_area', 'buildup_area', 'carpet_area', 'land_area', 'land_area1'].includes(d.field)
  );
  const plotSizeField = detailFields.find(d => d.field === 'plot_size');
  const cellFields = detailFields.filter(d =>
    ['bool', 'number', 'text'].includes(d.type)
    && d.field !== 'land_area_cent'
    && d.type !== 'area'
    && d.type !== 'computed'
  );
  const isFieldShown = (fieldKey) => {
    const aliases = fieldKey === 'corner_plot' ? ['corner_plot', 'is_corner'] : [fieldKey];
    const defn = propertydetailList.find((d) => aliases.includes(d.field));
    if (!defn) return true;
    return isDetailFieldVisible(defn, category_id, {
      isProject: !!is_project,
      knownCategoryIds,
    });
  };

  const resolveAreaValue = (field) => {
    if (ok(property[field])) return Number(property[field]);
    if (ok(ef[field])) return Number(ef[field]);
    // Form stores "Land Area" as land_area1 in extra_fields
    if (field === 'land_area' && ok(ef.land_area1)) return Number(ef.land_area1);
    // Legacy/project: land_area_cent value shows under Land Area with Cent unit
    if (field === 'land_area' && ok(property.land_area_cent)) return Number(property.land_area_cent);
    if (field === 'land_area' && ok(ef.land_area_cent)) return Number(ef.land_area_cent);
    return null;
  };

  /** Project forms store area as from/to ranges (often under land_area_cent_*). */
  const resolveAreaRange = (field) => {
    const fromCandidates = [
      ef[`${field}_from`],
      property[`${field}_from`],
      field === 'land_area' ? ef.land_area_cent_from : null,
      field === 'land_area' ? property.land_area_cent_from : null,
      field === 'land_area' ? ef.land_area1_from : null,
    ];
    const toCandidates = [
      ef[`${field}_to`],
      property[`${field}_to`],
      field === 'land_area' ? ef.land_area_cent_to : null,
      field === 'land_area' ? property.land_area_cent_to : null,
      field === 'land_area' ? ef.land_area1_to : null,
    ];
    let from = null;
    let to = null;
    for (const c of fromCandidates) {
      from = numOrNull(c);
      if (from != null) break;
    }
    for (const c of toCandidates) {
      to = numOrNull(c);
      if (to != null) break;
    }
    if (from == null && to == null) return null;
    return { from, to };
  };

  const resolveAreaUnit = (field) => {
    const candidates = [
      ef[`${field}_unit`],
      property[`${field}_unit`],
      field === 'land_area' ? ef.land_area1_unit : null,
      field === 'land_area' ? ef.land_area_unit : null,
      field === 'land_area' ? ef.land_area_cent_unit : null,
      field === 'land_area' ? property.land_area_cent_unit : null,
    ];
    for (const c of candidates) {
      if (c !== null && c !== undefined && c !== '') return Number(c);
    }
    // Default unit: Cent when value came from land_area_cent*, else sq-ft
    const fromCent =
      field === 'land_area'
      && !ok(property.land_area)
      && !ok(ef.land_area1)
      && (
        ok(property.land_area_cent)
        || ok(ef.land_area_cent)
        || ok(ef.land_area_cent_from)
        || ok(ef.land_area_cent_to)
        || ok(property.land_area_cent_from)
        || ok(property.land_area_cent_to)
      );
    if (fromCent) return 4;
    return 1;
  };

  // Get input labels from propertyInputs/projectInputs
  const allInputs = is_project ? projectInputs : propertyInputs;
  const inputMap = allInputs || {};

  // Synthetic land_area1 row when present in extra_fields and not covered by land_area AreaRow
  const landArea1Value = ok(ef.land_area1) ? Number(ef.land_area1) : null;
  const landArea1Unit = ef.land_area1_unit ? Number(ef.land_area1_unit) : null;
  const landAreaCoveredByAreaRow = areaFields.some((d) => {
    if (d.field !== 'land_area') return false;
    return resolveAreaValue('land_area') != null || resolveAreaRange('land_area') != null;
  });

  const landAreaRows = areaFields.map((defn) => {
    const range = resolveAreaRange(defn.field);
    const single = resolveAreaValue(defn.field);
    const unitId = resolveAreaUnit(defn.field);
    const allowed = getLandtypeIdsForAreaField(allInputs, defn.field, !!is_project);
    if (range && range.from != null && range.to != null && range.from !== range.to) {
      return {
        key: defn.id ?? defn.field,
        kind: 'range',
        label: defn.label,
        from: range.from,
        to: range.to,
        unitId,
        allowed,
        showLandTypeDropdown: isLandTypeDropdownVisible(defn, category_id, {
          isProject: !!is_project,
          knownCategoryIds,
        }),
      };
    }
    const raw = single ?? range?.from ?? range?.to ?? null;
    if (raw == null) return null;
    return {
      key: defn.id ?? defn.field,
      kind: 'single',
      label: defn.label,
      rawValue: raw,
      unitId,
      allowed,
      showLandTypeDropdown: isLandTypeDropdownVisible(defn, category_id, {
        isProject: !!is_project,
        knownCategoryIds,
      }),
    };
  }).filter(Boolean);

  const showOrphanLandArea1 =
    isFieldShown('land_area') && !landAreaCoveredByAreaRow && landArea1Value != null;
  const showLandAreaSection =
    landAreaRows.length > 0
    || showOrphanLandArea1
    || (plotSizeField && ok(plot_length) && ok(plot_breadth));

  // ✅ Extra fields from dynamic inputs — show non-empty, non-fixed values
  const SKIP_EXTRA = new Set([
    'thumbnail', 'slider', 'youtube_links', 'shorts_links', 'videos',
    'property_plan', 'amenities_id', 'custom_amenities', 'partner_id', 'sub_admin_id',
    'extra_fields', 'nearest_location', 'additional_info',
    'id', 'property_id', 'createdAt', 'updatedAt', '_id', '__v',
    'status_id', 'is_project', 'is_draft', 'user_id', 'admin_id', 'date',
    'role', 'is_own_property', 'is_urgent', 'is_highlighted', 'is_dtcp',
    'property_owner_name', 'property_owner_number',
    'property_owner_number_added_by_role', 'property_owner_number_added_by_name',
    'property_owner_number_added_by_id', 'property_owner_number_added_at',
    'slider_show', 'slider_work', 'booking', 'property_age', 'description',
    'sub_register_id', 'company_id', 'seo_title', 'seo_description',
    'type_id', 'category_id', 'city_id', 'state_id', 'latitude', 'longitude',
    // Shown in Area Details with proper land type
    'land_area1', 'land_area1_unit', 'land_area_unit',
    'buildup_area_unit', 'carpet_area_unit', 'plot_area_unit',
    'carpet_area', 'carpet_area_from', 'carpet_area_to',
    'buildup_area_from', 'buildup_area_to',
    'land_area_cent_from', 'land_area_cent_to', 'land_area_cent_unit',
    'plot_area', 'buildup_area', 'land_area', 'land_area_cent',
    'layout', 'layout_type', 'brochure', 'brochure_type',
    'bhk_custom', 'property_floor_no_custom', 'total_floors_custom',
    'address', 'address_details',
  ]);

  const isInternalExtraKey = (k) => {
    const key = String(k || '').toLowerCase();
    if (SKIP_EXTRA.has(k) || SKIP_EXTRA.has(key)) return true;
    // Hide staff/system ids that sometimes land in extra_fields
    if (/(^|_)(admin|partner|user|sub_admin|sub_register)_?id$/.test(key)) return true;
    if (key.includes('owner_number') || key.includes('owner_name')) return true;
    return false;
  };

  const descriptionText = (() => {
    const raw = property.description ?? extra_fields?.description;
    if (!ok(raw)) return null;
    return String(raw).trim();
  })();

  // Build extra details rows — from extra_fields + any non-empty top-level fields
  const extraRows = [];
  if (extra_fields && typeof extra_fields === 'object') {
    Object.entries(extra_fields).forEach(([k, v]) => {
      if (isInternalExtraKey(k)) return;
      // Skip raw unit ids (e.g. land_area1_unit: 4) — already folded into AreaRow
      if (k.endsWith('_unit') || k.endsWith('_custom')) return;
      const inputDef = inputMap[k];
      const label = inputDef?.label || k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      if (k === 'is_corner' || k === 'gated_community') {
        const yn = formatYesNo(v);
        if (yn) extraRows.push({ key: k, label, value: yn });
        return;
      }
      if (!ok(v)) return;
      extraRows.push({ key: k, label, value: String(v) });
    });
  }

  // Amenities
  const allAmenityValues = [...(amenities_id || []), ...(custom_amenities || [])];
  const amGroups = {};
  allAmenityValues.forEach(val => {
    if (typeof val === 'number') {
      const am = aminities.find(a => a.id === val); if (!am) return;
      const cat = aminitiesCategories?.find(c => c.id === am.category_id);
      const k = cat?.name || 'Other';
      if (!amGroups[k]) amGroups[k] = [];
      amGroups[k].push(am.aminitie_name);
    } else if (typeof val === 'string' && val.trim()) {
      if (!amGroups['Custom']) amGroups['Custom'] = [];
      amGroups['Custom'].push(val.trim());
    }
  });

  // Videos — use saved videos array with thumbnails
  const videoList = videos?.length
    ? videos
    : (youtube_links || []).map(url => ({ video_link: url, thumbnail: null }));

  const nearestRows = normalizeNearestLocation(nearest_location ?? property.nearest_location);

  const resolveNearName = (nearId) => {
    const matched = nearestlocation?.find((nl) => String(nl.id) === String(nearId));
    return matched?.near_name || null;
  };

  const hasAmenities = Object.keys(amGroups).length > 0;
  const hasNearest = nearestRows.length > 0;
  const hasVideos = videoList.length > 0;
  const hasShorts = shorts_links?.length > 0;
  const hasPlans = property_plan?.length > 0;
  const visibleExtraRows = extraRows.filter((row) => {
    if (String(row.label || '').toLowerCase() === 'description') return false;
    const fieldKey = row.key === 'is_corner' ? 'corner_plot' : row.key;
    if (fieldKey && !isFieldShown(fieldKey)) return false;
    return hasDetailCellValue(row.value);
  });
  const hasExtra = visibleExtraRows.length > 0;
  const showFloor = !!(floorLabel && isFieldShown('property_floor_no'));
  const showTotalFloors = !!(totalFloorsLabel && isFieldShown('total_floors'));
  const showGated = hasDetailCellValue(gatedLabel, 'bool') && isFieldShown('gated_community');
  const showAge = !!(propertyAgeLabel && isFieldShown('property_age'));
  const visibleCellFields = cellFields
    .filter((d) => !['total_floors', 'property_floor_no', 'gated_community'].includes(d.field))
    .map((defn) => {
      if (defn.field === 'corner_plot') {
        const raw = property.corner_plot ?? extra_fields?.is_corner;
        const yn = formatYesNo(raw);
        if (!hasDetailCellValue(yn)) return null;
        return { key: defn.id, label: defn.label, value: yn };
      }
      if (defn.field === 'address') {
        const value = resolveAddressDisplay(property, extra_fields);
        if (!hasDetailCellValue(value, defn.type)) return null;
        return { key: defn.id, label: defn.label, value, type: defn.type };
      }
      if (defn.field === 'address_details') {
        const value = property.address_details ?? extra_fields?.address_details;
        if (!hasDetailCellValue(value, defn.type)) return null;
        const text = String(value).trim();
        if (/^https?:\/\//i.test(text) || /^www\./i.test(text)) return null;
        return { key: defn.id, label: defn.label, value: text, type: defn.type };
      }
      const value = property[defn.field];
      if (!hasDetailCellValue(value, defn.type)) return null;
      return { key: defn.id, label: defn.label, value, type: defn.type };
    })
    .filter(Boolean);
  const showPropertyDetailsSection =
    showFloor || showTotalFloors || showGated || showAge || visibleCellFields.length > 0;
  const hasDescription = !!descriptionText;
  const primaryArea = property.plot_area || property.land_area || property.buildup_area || null;

  const hasBrochure = brochureArr.length > 0;
  const hasLayout = layoutArr.length > 0;
  const hasCompany =
    ok(property.company_logo) ||
    ok(property.company_name) ||
    ok(property.company_description);



  const priceDisplay = getPriceDisplay(property);
  const downloadAllBrochures = async () => {
    for (let i = 0; i < brochureArr.length; i++) {
      const file = brochureArr[i];
      const url = imgUrl ? imgUrl(file) : file;
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        const ext = file.split('.').pop();
        link.download = `brochure-${i + 1}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error("Download failed:", err);
      }
    }
  };

  const handleWishlist = () => {
    const result = toggleWishlist(property.id, {
      label: propertyActivityLabel(property, 'Added to wishlist'),
      url: `/properties/${property.id}`,
    });
    if (!result) navigate('/login');
    else !isWished ? toast.success('Added to Wishlist') : toast.info('Removed from Wishlist');
  };


  // After the destructuring that has: brochure,
  // Add this line immediately after the destructuring block closes:

  return (
    <div className="pd-page">
      <PropertyAppPrompt
        propertyId={property.id}
        slug={openAppSlug}
        isProject={!!property.is_project}
      />
      {/* Top Bar */}
      <div className="pd-topbar">
        {(soldOut || is_urgent || is_highlighted || is_project) ? (
          <div className="pd-badges">
            {soldOut && (
              <span className="pd-badge pd-sold-out">
                <span className="pd-sold-out-dot" aria-hidden="true" />
                Sold Out
              </span>
            )}
            {is_urgent && <span className="pd-badge pd-urg"><AppIcon name="urgent" size={12} /> Urgent</span>}
            {is_highlighted && <span className="pd-badge pd-feat"><AppIcon name="featured" size={12} /> Featured</span>}
            {is_project && <span className="pd-badge pd-proj"><AppIcon name="project" size={12} /> Project</span>}
          </div>
        ) : null}
        <div className="pd-topbar-right">
          {/* ✅ Property ID — auto from DB */}
          {ok(property_id) && <span className="pd-pid">#{property_id}</span>}
          {ok(date) && <span className="pd-date">{date}</span>}
          <button className="pd-top-btn" onClick={() => setShareOpen(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
          <button className={`pd-top-btn pd-top-wish ${isWished ? 'pd-top-wish-active' : ''}`} onClick={handleWishlist}>
            <svg viewBox="0 0 24 24" fill={isWished ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" width="15" height="15">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            {isWished ? 'Wishlisted' : 'Wishlist'}
          </button>
        </div>
      </div>

      {soldOut && (
        <div className="pd-sold-out-banner" role="status">
          <div className="pd-sold-out-banner__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="pd-sold-out-banner__body">
            <p className="pd-sold-out-banner__title">This property has been sold out</p>
            <p className="pd-sold-out-banner__desc">
              You can still view full details, but enquiry, booking, and contact are unavailable.
            </p>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="pd-hero">
        <div className={`pd-gal-col${soldOut ? ' pd-gal-col--sold-out' : ''}`}>
          {images.length > 0 && <Gallery images={images} />}
        </div>

        <div className={`pd-det-col${is_dtcp ? ' pd-det-col--has-dtcp' : ''}`}>
          {is_dtcp && (
            <div className="pd-dtcp-badge-wrap" aria-hidden="true">
              <img src={dtcpBadge} alt="DTCP Approved" className="pd-dtcp-badge" />
            </div>
          )}
          {(ok(locationStr) || catName || bhkLabel) && (
            <div className="pd-header-meta">
              <div className="pd-header-meta__text">
                {ok(locationStr) && (
                  <p className="pd-loc">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                    {locationStr}
                  </p>
                )}
                {(catName || bhkLabel) && (
                  <h1 className="pd-title">{catName}{bhkLabel ? ` — ${bhkLabel}` : ''}</h1>
                )}
              </div>
            </div>
          )}

          {priceDisplay && (
            <div className="pd-price-row">
              <span className="pd-price">{priceDisplay}</span>
              {ok(price) && ok(primaryArea) && (
                <span className="pd-pps">
                  {fmt(Math.round(price / primaryArea))}
                  <sub>/sq-ft</sub>
                </span>
              )}
            </div>
          )}

          <div className="pd-pills">
            {ok(sales_type) && <span className="pd-pill">{sales_type}</span>}
            {availabilityLabel && (
              <span className={`pd-pill ${availabilityLabel === 'Ready to Move' ? 'pill-green' : 'pill-amber'}`}>
                <AppIcon
                  name="circle"
                  size={10}
                  color={availabilityLabel === 'Ready to Move' ? '#16a34a' : '#f59e0b'}
                />{' '}
                {availabilityLabel}
              </span>
            )}
            {directionLabel && <span className="pd-pill"><AppIcon name="compass" size={12} /> {directionLabel}</span>}
            {furnishingLabel && <span className="pd-pill"><AppIcon name="furnishing" size={12} /> {furnishingLabel}</span>}
          </div>

          <div className="det-table">
            {showLandAreaSection && (
              <>
                <div className="det-group-hd">Land Area Details</div>
                {landAreaRows.map((row) => (
                  row.kind === 'range' ? (
                    <RangeAreaRow
                      key={row.key}
                      label={row.label}
                      from={row.from}
                      to={row.to}
                      unitId={row.unitId}
                    />
                  ) : (
                    <AreaRow
                      key={row.key}
                      label={row.label}
                      rawValue={row.rawValue}
                      unitId={row.unitId}
                      totalPrice={price}
                      allowedLandTypeIds={row.allowed}
                      showLandTypeDropdown={row.showLandTypeDropdown}
                    />
                  )
                ))}
                {showOrphanLandArea1 && (
                  <AreaRow
                    label="Land Area"
                    rawValue={landArea1Value}
                    unitId={landArea1Unit}
                    totalPrice={price}
                    allowedLandTypeIds={getLandtypeIdsForAreaField(allInputs, 'land_area', !!is_project)}
                    showLandTypeDropdown={isLandTypeDropdownVisible(
                      (Array.isArray(propertydetail) ? propertydetail : []).find((d) => d.field === 'land_area'),
                      category_id,
                      { isProject: !!is_project, knownCategoryIds }
                    )}
                  />
                )}
                {plotSizeField && ok(plot_length) && ok(plot_breadth) && (
                  <DetRow label={plotSizeField.label} value={`${plot_length} × ${plot_breadth} ft`} />
                )}
              </>
            )}

            {showPropertyDetailsSection && (
              <>
                <div className="det-group-hd">Property Details</div>
                <div className="det-2col">
                  {showFloor && <DetailCell label="Floor" value={floorLabel} />}
                  {showTotalFloors && <DetailCell label="Total Floors" value={totalFloorsLabel} />}
                  {showGated && <DetailCell label="Gated" value={gatedLabel} type="bool" />}
                  {showAge && (
                    <DetailCell
                      label="Property Age"
                      value={propertyAgeLabel}
                    />
                  )}

                  {visibleCellFields.map((item) => (
                    <DetailCell key={item.key} label={item.label} value={item.value} type={item.type} />
                  ))}
                </div>
              </>
            )}

            {/* ✅ Extra dynamic fields from extra_fields */}
            {hasExtra && (
              <>
                <div className="det-group-hd">Additional Details</div>
                <div className="det-2col">
                  {visibleExtraRows.map((row, i) => (
                    <DetailCell key={row.key || i} label={row.label} value={row.value} />
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="pd-cta-desk">
            {!listingActive ? (
              <p className={`pd-cta-inactive${soldOut ? ' pd-cta-sold-out' : ''}`}>
                {soldOut ? SOLD_OUT_MSG : LISTING_INACTIVE_MSG}
              </p>
            ) : null}
            <div className="pd-cta-row">
              <button className="cta-book" onClick={() => listingActive && setBookingOpen(true)} disabled={ctaDisabled} aria-disabled={ctaDisabled}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                BOOK A SITE VISIT
              </button>
              <button className="cta-call" onClick={() => listingActive && setModal({ open: true, title: 'Call / WhatsApp' })} disabled={ctaDisabled} aria-disabled={ctaDisabled}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.5 1.17 2 2 0 012.5.5h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.01a16 16 0 006.06 6.06l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                </svg>
                CALL / WHATSAPP
              </button>
              <button
                className="cta-enquiry"
                onClick={() => listingActive && setModal({ open: true, title: 'Send Enquiry' })}
                disabled={ctaDisabled}
                aria-disabled={ctaDisabled}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                SEND ENQUIRY
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="pd-body">

        {/* Description — full width below hero */}
        {hasDescription && (
          <section className="pd-sec pd-desc-sec">
            <div className="sec-head">Description</div>
            <div className="pd-desc-body">{descriptionText}</div>
          </section>
        )}

        {/* Shorts */}
        {hasShorts && (
          <section className="pd-sec">
            <div className="sec-head">Property Shorts</div>
            <div className="shorts-strip">
              {shorts_links.map((url, i) => {
                const vid = getYTId(url);
                return (
                  <button key={i} className="story-circle" onClick={() => setStory({ open: true, idx: i })}>
                    <div className="story-ring">
                      <div className="story-av">
                        {vid
                          ? <ImageWithLoader src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`} alt="" onError={e => { e.target.style.display = 'none'; }} />
                          : <span className="story-av-icon">▶</span>
                        }
                      </div>
                    </div>
                    <span className="story-lbl">Short {i + 1}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Amenities + Videos */}
        {(hasAmenities || hasVideos) && (
          <div className="av-flex">
            {hasAmenities && (
              <section className="pd-sec av-am">
                <div className="sec-head">Amenities</div>
                {Object.entries(amGroups).map(([cat, items]) => (
                  <div key={cat} className="am-grp">
                    <div className="grp-lbl">{cat}</div>
                    <div className="am-chips">
                      {items.map(n => (
                        <span key={n} className="am-chip">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="9" height="9">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                          {n}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            )}

            {hasVideos && (
              <section className="pd-sec av-yt">
                <div className="sec-head">Property Videos</div>
                <div className="yt-list">
                  {videoList.map((v, i) => {
                    const vid = getYTId(v.video_link);
                    // ✅ Use saved thumbnail or YT auto-thumb
                    const thumb = v.thumbnail
                      ? (imgUrl ? imgUrl(v.thumbnail) : v.thumbnail)
                      : (vid ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg` : null);
                    return (
                      <div key={i} className="yt-item" onClick={() => setYtId(vid || v.video_link)}>
                        <div className="yt-thumb">
                          {thumb
                            ? <ImageWithLoader src={thumb} alt="" className="yt-thumb-img" onError={e => { e.target.style.display = 'none'; }} />
                            : <div className="yt-thumb-ph" />
                          }
                          <div className="yt-play-layer">
                            <div className="yt-play-circle">
                              <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                                <polygon points="5 3 19 12 5 21 5 3" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="yt-info">
                          <div className="yt-title">Property Tour {i + 1}</div>
                          <div className="yt-sub">Tap to watch</div>
                        </div>
                        <div className="yt-arrow">›</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Nearest Location */}
        {hasNearest && (
          <section className="pd-sec">
            <div className="sec-head">Nearest Location</div>
            <div className="nl-table">
              <div className="nl-head">
                <span>Nearest Location</span>
                <span>Distance (km)</span>
              </div>
              {nearestRows.map((row, i) => {
                const name = resolveNearName(row.nearId) || `Location ${i + 1}`;
                return (
                  <div key={`${row.nearId}-${i}`} className="nl-row">
                    <span className="nl-name">{name}</span>
                    <span className="nl-dist">{row.distance} km</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Property Plans */}
        {hasPlans && (
          <section className="pd-sec">
            <div className="sec-head">Property Plans</div>
            <div className="plans-grid">
              {property_plan.map((plan, i) => (
                <div key={i} className="plan-card">
                  {plan.image && (
                    <div className="plan-img-wrap">
                      <img src={imgUrl ? imgUrl(plan.image) : plan.image} alt={plan.title || `Plan ${i + 1}`} className="plan-img" />
                    </div>
                  )}
                  {(plan.title || plan.description) && (
                    <div className="plan-info">
                      {plan.title && <div className="plan-title">{plan.title}</div>}
                      {plan.description && <div className="plan-desc">{plan.description}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Layout */}
        {hasLayout && (
          <DocMediaSection
            title="Layout"
            files={layoutArr}
            index={layoutIndex}
            setIndex={setLayoutIndex}
            typeHint={layout_type || ef.layout_type}
            imgUrl={imgUrl}
          />
        )}

        {/* Brochure */}
        {hasBrochure && (
          <DocMediaSection
            title="Brochure"
            files={brochureArr}
            index={brochureIndex}
            setIndex={setBrochureIndex}
            typeHint={brochure_type || ef.brochure_type}
            imgUrl={imgUrl}
            showDownloadAll
            onDownloadAll={downloadAllBrochures}
          />
        )}



        {/* Company Profile */}
        {hasCompany && (
          <section className="pd-sec">
            <div className="sec-head">Company Profile</div>

            <button
              type="button"
              className={`company-card-v2 company-card-v2--link${property.company_id ? '' : ' is-disabled'}`}
              onClick={() => {
                if (property.company_id) navigate(`/company/${property.company_id}`);
                else toast.info('Company profile link is not available for this listing yet.');
              }}
              aria-label={
                property.company_name
                  ? `View all listings by ${property.company_name}`
                  : 'View company listings'
              }
            >
              {/* LEFT: Logo */}
              <div className="company-left">
                {ok(property.company_logo) ? (
                  <div className="company-logo-frame">
                    <img
                      src={imgUrl ? imgUrl(property.company_logo) : property.company_logo}
                      alt=""
                      className="company-logo"
                    />
                  </div>
                ) : ok(property.company_name) ? (
                  <div className="company-logo-frame company-logo-fallback">
                    {property.company_name
                      .split(" ")
                      .slice(0, 2)
                      .map((w) => w[0])
                      .join("")
                      .toUpperCase()}
                  </div>
                ) : null}
              </div>

              {/* RIGHT: Content */}
              <div className="company-right">
                <div className="company-top">
                  {ok(property.company_name) && (
                    <h3 className="company-name">{property.company_name}</h3>
                  )}
                  {ok(property.company_name) && (
                    <span className="company-badge">
                      <AppIcon name="verified" size={12} /> Verified
                    </span>
                  )}
                </div>

                {ok(property.company_description) && (
                  <p className="company-desc">{property.company_description}</p>
                )}

                <span className="company-card-cta">
                  View all properties <span aria-hidden="true">→</span>
                </span>
              </div>
            </button>
          </section>
        )}

      </div>

      {/* ── Related Properties ─────────────────────────────────── */}
      <RelatedProperties
        currentPropertyId={property.id}
        categoryId={category_id}
      />

      {/* Mobile Bar */}
      <div className={`mob-bar${soldOut ? ' mob-bar--sold-out' : ''}${bookingOpen ? ' hide-bar' : ''}`}>
        <button className="mob-call" onClick={() => listingActive && setModal({ open: true, title: 'Call / WhatsApp' })} disabled={ctaDisabled} aria-disabled={ctaDisabled}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.5 1.17 2 2 0 012.5.5h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.01a16 16 0 006.06 6.06l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
          </svg>
          CALL
        </button>
        <button className="mob-enquiry" onClick={() => listingActive && setModal({ open: true, title: 'Send Enquiry' })} disabled={ctaDisabled} aria-disabled={ctaDisabled}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          ENQUIRY
        </button>
        <button className="mob-book" onClick={() => listingActive && setBookingOpen(true)} disabled={ctaDisabled} aria-disabled={ctaDisabled}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          SITE VISIT
        </button>
      </div>

      {/* Update your modal rendering section to this */}

      {/* Only show ContactModal if it's NOT an enquiry */}
      {modal.open && modal.title !== 'Send Enquiry' && (
        <ContactModal
          open={modal.open}
          onClose={() => setModal({ open: false, title: '' })}
          contacts={contacts}
          title={modal.title}
          property={property}
          isMobile={isMobile}
        />
      )}

      {/* Only show EnquiryModal if the title IS 'Send Enquiry' */}
      {modal.open && modal.title === 'Send Enquiry' && (
        <EnquiryModal
          property={property}
          onClose={() => setModal({ open: false, title: '' })}
        />
      )}
      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} property={property} />
      <SharePopup
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareText={`${catName}${priceDisplay ? ` — ${priceDisplay}` : ''}${locationStr ? `\n📍 ${locationStr}` : ''}`}
        shareUrl={buildPropertyWebShareUrl({
          propertyId: property.id,
          categoryName: catName,
          cityName: city?.city_name,
        })}
        propertyId={property.id}
      />
      {/* ✅ EnquiryModal — properly integrated */}
      {/* {showEnquiry && <EnquiryModal property={property} onClose={() => setShowEnquiry(false)}/>} */}
      {ytId && <YTModal videoId={ytId} onClose={() => setYtId(null)} />}
      {story.open && (
        <StoryViewer links={shorts_links} startIdx={story.idx} onClose={() => setStory({ open: false, idx: 0 })} />
      )}
    </div>
  );
};

export default PropertyDetails;